import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Button } from "./ui/button";

type Faq = { question: string; answer: ReactNode };

const faqs: Faq[] = [
  {
    question: "Como funciona a análise de preços?",
    answer: (
      <>
        Usamos a tabela FIPE como referência e aplicamos um modelo que considera estado do veículo
        (amassados/arranhões), histórico de leilões e tendências de mercado para calcular um preço-alvo entre{" "}
        <b>100% e 70% da FIPE</b>, conforme o nível de dano.
      </>
    ),
  },
  {
    question: "Posso usar a câmera do celular para escanear o carro?",
    answer: (
      <>
        Sim. Na{" "}
        <Link to="/demo" className="text-primary underline">
          página de Demo
        </Link>{" "}
        você poderá testar o fluxo: apontar a câmera para o veículo, capturar as áreas relevantes e deixar a IA estimar
        o impacto no valor.
      </>
    ),
  },
  {
    question: "Meus dados estão seguros?",
    answer: (
      <>
        Sim! As análises rodam no navegador quando possível e só enviamos imagens para processamento em nuvem com seu
        consentimento. Não coletamos dados pessoais sensíveis.
      </>
    ),
  },
  {
    question: "De onde vêm os dados da FIPE?",
    answer: <>Atualizamos mensalmente a FIPE a partir da fonte oficial, garantindo precisão nas estimativas.</>,
  },
  {
    question: "Existe um limite de consultas?",
    answer: (
      <>
        O plano <b>Grátis</b> permite análises básicas. Planos pagos liberam histórico na nuvem, alertas, análise
        preditiva e exportação. Veja os{" "}
        <Link to="/precos" className="text-primary underline">
          planos
        </Link>
        .
      </>
    ),
  },
  {
    question: "Qual a precisão da análise?",
    answer: (
      <>
        Em cenários com boas imagens e iluminação, nossa precisão típica fica em torno de <b>±2–5%</b> do preço-alvo
        estimado. Resultados podem variar conforme qualidade das fotos e condições do veículo.
      </>
    ),
  },
];

export function FAQ() {
  // JSON-LD para SEO (FAQPage)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: typeof f.question === "string" ? f.question : "",
      acceptedAnswer: {
        "@type": "Answer",
        text: typeof f.answer === "string" ? f.answer : "Veja detalhes na página.",
      },
    })),
  };

  return (
    <section className="py-20 md:py-32 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">Perguntas Frequentes</h2>
          <p className="text-lg text-muted-foreground">Tire suas dúvidas sobre nossa plataforma</p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.07 }}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className="bg-background border border-border/50 rounded-lg px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <span className="font-heading font-semibold">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">{faq.answer}</AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>

          {/* CTA abaixo do FAQ */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="/demo">Testar a Demo</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/precos">Ver Planos</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* SEO: schema.org FAQPage */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </section>
  );
}
